import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { upsellClass, upsellSurfaceClass } from 'components/portal/upsell';
import { formatDayMonth } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import { bestAnalysisPlan } from 'modules/portal-plans/bestAnalysisPlan';
import { usePortalPlans } from 'modules/portal-plans/usePortalPlans';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';

/**
 * The analysis credits left this period, with the plan upgrade as a gradient button. Without a running
 * plan it says what a plan unlocks instead. The upgrade label names the real plan with the most analyses.
 */
export function HomeCredits() {
  const { t } = useTranslation();
  const { data: subscription } = usePortalSubscription();
  const { data: plans } = usePortalPlans(true);
  const [open, setOpen] = useState(false);

  const active = Boolean(subscription && new Date(subscription.periodEnd).getTime() > Date.now());
  const limit = active ? subscription?.analysisLimit ?? 0 : 0;
  const left = Math.max(0, subscription?.analysesRemaining ?? 0);
  const hasCredits = active && limit > 0;

  const best = bestAnalysisPlan(plans, limit);
  const cta = best ? t('portal.credits_cta_plan', { name: best.name, count: best.count }) : t('portal.credits_cta_plans');

  return (
    <section
      aria-labelledby="home-credits-title"
      className={cn('flex flex-wrap items-center justify-between gap-5 rounded-[20px] border px-5 py-5 md:px-6', upsellSurfaceClass)}
    >
      <div className="min-w-0 flex-1 basis-[260px]">
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#7C2D9E] dark:text-fuchsia-300">
          {subscription && active ? `${subscription.planName} · ${t('portal.credits_eyebrow')}` : t('portal.credits_eyebrow')}
        </p>
        {hasCredits ? (
          <>
            <p id="home-credits-title" className="mt-2 font-display text-2xl font-extrabold tracking-[-0.03em] text-foreground">
              <span className="tabular-nums">{left} / {limit}</span>{' '}
              <span className="text-sm font-semibold tracking-normal text-muted-foreground">{t('portal.credits_left')}</span>
            </p>
            <div
              role="progressbar"
              aria-label={t('portal.credits_eyebrow')}
              aria-valuemin={0}
              aria-valuemax={limit}
              aria-valuenow={left}
              className="mt-3 h-2 overflow-hidden rounded-full bg-[#7C2D9E]/15"
            >
              <div
                className="h-full rounded-full bg-[linear-gradient(90deg,#4F2BE0,#DB2777)]"
                style={{ width: `${Math.min(100, (left / limit) * 100)}%` }}
              />
            </div>
            <p className="mt-2.5 text-[12.5px] text-muted-foreground">
              {t('portal.credits_hint', { date: subscription ? formatDayMonth(subscription.periodEnd) : '' })}
            </p>
          </>
        ) : (
          <>
            <p id="home-credits-title" className="mt-2 font-display text-xl font-extrabold tracking-[-0.03em] text-foreground">
              {t('portal.credits_none_title')}
            </p>
            <p className="mt-2 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">{t('portal.credits_none_hint')}</p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn('inline-flex h-11 items-center justify-center rounded-xl px-5 text-sm font-bold', upsellClass)}
      >
        {cta}
      </button>
      {open && <PlanCheckoutModal open onOpenChange={setOpen} subscription={subscription} />}
    </section>
  );
}

export default HomeCredits;
